/**
 * The preview shell's parse pipeline: TSCN text → `SceneGraph` or an error. `parseTscnContent` is
 * pure, so it is tested without a React tree.
 */
import { useMemo } from 'react';
import { TscnParser } from '../../parser/TscnParser.js';
import { tscnSceneToParsedScene, buildSceneGraph } from '../../core/SceneGraph.js';
import { isGltfPath } from '../../godot/import/gltf.js';
import { synthesiseGLBScene } from '../../resources/processors/createSceneProcessor.js';
export function parseTscnContent(content, rootScenePath) {
    // A top-level .glb/.gltf is not TSCN text. Synthesise the single GLBSceneRoot that
    // createSceneProcessor's instanced path uses. The node fetches the bytes through
    // useResource('GLBMesh').
    if (isGltfPath(rootScenePath)) {
        return toParseResult(rootScenePath, synthesiseGLBScene(rootScenePath));
    }
    if (!content) {
        return { sceneGraph: null, error: null };
    }
    try {
        const parser = new TscnParser();
        const tscnScene = parser.parse(content);
        // The lenient `TscnParser` salvages what it can. Text that yields zero nodes is probably a
        // broken file, so it is an error rather than "No nodes to display".
        if (tscnScene.nodes.length === 0 && content.trim().length > 0) {
            return {
                sceneGraph: null,
                error: 'Parser could not extract any nodes from the content. The file may be malformed.',
            };
        }
        return toParseResult(rootScenePath, tscnScene);
    }
    catch (err) {
        return {
            sceneGraph: null,
            error: err instanceof Error ? err.message : String(err),
        };
    }
}
function toParseResult(rootScenePath, tscnScene) {
    const parsedScene = tscnSceneToParsedScene(rootScenePath, tscnScene.nodes, tscnScene.externalResources, tscnScene.internalResources, tscnScene.editableInstances);
    return { sceneGraph: buildSceneGraph(parsedScene), error: null };
}
/** Re-parses only when `content` or `rootScenePath` changes. */
export function useParsedScene(content, rootScenePath) {
    return useMemo(() => parseTscnContent(content, rootScenePath), [content, rootScenePath]);
}
//# sourceMappingURL=useParsedScene.js.map